import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import {
  api, ApiError, getRefreshToken, getToken, onAuthLost, setRefreshToken, setToken,
  clearActivity, isSessionExpiredByIdle, touchActivity,
  setSessionPolicy, setTenantIdleBase, setPrefill, scheduleSilentRefresh, stopSilentRefresh,
} from '../lib/api';
import { postLoginPath } from './updateCardLink';
import { SIGNUP_SLUG_KEY } from './signupHelpers';
import { SessionExpiryWarning } from './SessionExpiryWarning';

/** Fase 0 — flag (sessionStorage) para que LoginScreen avise "sesión expirada". */
export const SESSION_EXPIRED_FLAG = 'ag_stratos.sessionExpired';

export type UserRole = 'SUPER_USER' | 'COMMON_USER';

export interface MeResponse {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  /** Day 5 Block A: permisos del rol, evaluados por hasPermission() en UI. */
  permissions: string[];
  /** Day 5 Block B: TRUE solo para el owner de Stratos. Activa la sección PLATAFORMA. */
  isPlatformAdmin: boolean;
  /** V217 (§1ter): el usuario es el TITULAR de la cuenta (cobros, datos fiscales, avisos). */
  isOwner?: boolean;
  /** V217: nombre del titular, para "pídeselo a X". */
  ownerName?: string | null;
  /** V217 (D16): soporte congeló la titularidad (acciones del titular bloqueadas). */
  ownerFrozen?: boolean;
  /** v1.3.2: el email fue confirmado (los users pre-v1.3.2 = true). */
  emailConfirmed: boolean;
  /** v1.3.2: el user debe cambiar su contraseña antes de entrar al app (Opción B). */
  mustChangePassword: boolean;
  /** V45: el usuario ya vio el tour guiado (auto-inicia solo si false). */
  tourCompleted?: boolean;
  tenant: {
    id: string;
    name: string;
    slug: string;
    subscriptionPlan: string;
    /** §17.3 — eje comercial/billing (preset). El ACCESO lo define enabledModules. */
    plan?: string;
    /** feat/modular-plans — módulos habilitados (fuente de verdad del acceso/sidebar). */
    enabledModules?: string[];
    branding: {
      brandName: string;
      brandLogoUrl: string | null;
      primaryColor: string;
    };
    // V11 / Day 4
    trialEndsAt: string | null;
    subscriptionActive: boolean;
    onboardingCompletedAt: string | null;
    /** V12 Block D: moneda de visualización del tenant. */
    displayCurrency: string;
    /** §17.6 (V35): estado de trial derivado server-side (banner + gate). */
    trialStatus?: { daysLeft: number | null; expired: boolean; active: boolean };
    /** T4 (V212, TyC §8.8): cobro rechazado en curso / suspensión por cobranza. */
    billing?: import('./billingNotice').TenantBillingState;
    /** V222 (C-03/C-17): aviso de facturación del banner (solo SUPER_USER; null = nada). */
    cfdiNotice?: import('./cfdiNotice').CfdiNotice | null;
    /** V128 (Carril K): ventana de inactividad del tenant en minutos (60/240/480/720). */
    sessionIdleMinutes?: number;
    /** V129 (§30): redondear el cambio en efectivo hacia arriba a $0.50 (POS). */
    cashRoundingEnabled?: boolean;
    /** V131 (§31.2): umbral del semáforo de comandas del tenant (min). 10/15/20. */
    kitchenAlertMinutes?: number;
    /** V169 (🅑): separación Cocina vs Barra activa. false = una sola línea (default). */
    kitchenTwoStations?: boolean;
  };
  /** V76 (multisucursal): activa = claim del token; home = asignada al usuario. */
  branch?: { activeBranchId: string | null; homeBranchId: string | null };
  /** V79 — gate de re-aceptación legal: true = debe re-aceptar los docs
   *  vigentes antes de usar el app (App.tsx bloquea con el gate). */
  legal?: {
    needsReacceptance: boolean;
    currentVersions: { terms: string; privacyNotice: string };
  };
  /** 1a — kill-switch del billing (server-side, BILLING_CHECKOUT_ENABLED).
   *  FAIL-CLOSED: ausente = false → los CTAs de "Suscribirme" NO se muestran
   *  (billing dormido en prod hasta el go-live 1c). */
  billing?: { checkoutEnabled: boolean };
}

export interface SignupPayload {
  email: string;
  password: string;
  fullName: string;
  tenantName: string;
  /** §1bis.3 — ya no se pide: el backend lo genera del nombre del negocio. */
  tenantSlug?: string;
  /** Fase 0 — aceptación obligatoria de T&C + Aviso de Privacidad. */
  acceptedLegal: boolean;
  /** Alta 2 (a) — la casilla única también atesta "Soy mayor de 18 años". */
  ageAttested?: boolean;
  /** V78 — opt-in OPCIONAL de comunicaciones de marketing (no premarcado). */
  marketingOptIn?: boolean;
  /** V80 (1a) — código de referido OPCIONAL (−10% 1er periodo si es válido). */
  referralCode?: string;
  /** feat/turnstile — token anti-bot; solo viaja si el widget está activo. */
  turnstileToken?: string;
}

interface AuthCtx {
  token: string | null;
  user: MeResponse | null;
  loading: boolean;
  login: (email: string, password: string, tenantSlug: string, opts?: { remember?: boolean; turnstileToken?: string }) => Promise<void>;
  signup: (payload: SignupPayload) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>({} as AuthCtx);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTok] = useState<string | null>(getToken());
  const [user, setUser] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(!!getToken());

  const refresh = useCallback(async () => {
    if (!getToken()) { setUser(null); return; }
    try {
      const me = await api<MeResponse>('/me');
      // V128 (Carril K) — aplica la ventana de inactividad del tenant. Se re-evalúa
      // en cada /me (login, reload y tras cambiarla en Configuración) → toma efecto
      // sin re-login.
      setTenantIdleBase(me.tenant?.sessionIdleMinutes);
      setUser(me);
    } catch (e) {
      // Carril K — NO tumbar la sesión por un blip: solo un 401 (auth realmente
      // perdida; withAuthRetry ya intentó refrescar) limpia tokens. Errores
      // transitorios (red/5xx) o el gate de trial (403) dejan la sesión intacta.
      if (e instanceof ApiError && e.status === 401) {
        setToken(null); setTok(null); setRefreshToken(null); setUser(null);
      }
    }
  }, []);

  // Re-hidratar /me al montar si quedó token persistido.
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    // Fase 0 — gate de inactividad ANTES de validar /me: si la sesión quedó
    // inactiva más del límite (típico al reabrir desde el historial), forzamos
    // login en vez de refrescar el access en silencio.
    if (isSessionExpiredByIdle()) {
      sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1');
      setToken(null); setTok(null); setRefreshToken(null); clearActivity();
      setUser(null); setLoading(false);
      return;
    }
    setLoading(true);
    refresh().finally(() => setLoading(false));
  }, [token, refresh]);

  const login = useCallback(async (
    email: string, password: string, tenantSlug: string,
    opts?: { remember?: boolean; turnstileToken?: string },
  ) => {
    setLoading(true);
    try {
      const res = await api<{ accessToken: string; refreshToken?: string; expiresIn?: number }>('/auth/login', {
        method: 'POST',
        // feat/turnstile: el token solo viaja si el widget está activo (sin
        // VITE_TURNSTILE_SITE_KEY no se manda nada y el backend no lo exige).
        body: { email, password, tenantSlug, ...(opts?.turnstileToken ? { turnstileToken: opts.turnstileToken } : {}) },
      });
      setToken(res.accessToken);
      // v1.3.2 — en el flujo must_change_password el backend NO emite refresh aún;
      // solo lo guardamos si vino. /me revelará mustChangePassword y el Shell
      // enruta a la pantalla de cambio forzado.
      if (res.refreshToken) setRefreshToken(res.refreshToken);
      // Carril K — política de sesión: "recordar" (opt-in, default off) → ventana
      // de inactividad larga (12h); sin marcar → base 1h. + pre-fill tenant+correo
      // (NO la contraseña) para no re-teclear al volver.
      setSessionPolicy(opts?.remember ?? false);
      setPrefill(tenantSlug, email);
      // Sellar actividad para que el gate de inactividad no eche al user recién
      // logueado por una marca vieja de una sesión anterior.
      touchActivity();
      sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
      // Day 4 hotfix BUG#2: full reload garantiza state limpio. T4: conserva la liga del
      // correo de cobro rechazado (/pago/actualizar) para abrir el portal tras el login.
      window.location.href = postLoginPath(window.location.pathname);
    } finally {
      setLoading(false);
    }
  }, []);

  const signup = useCallback(async (payload: SignupPayload) => {
    setLoading(true);
    try {
      const res = await api<{ accessToken: string; refreshToken: string; expiresIn?: number; tenant?: { slug?: string } }>('/auth/signup', {
        method: 'POST',
        body: payload,
      });
      // §1bis (decisión Arturo 08/10) — el login sigue pidiendo el identificador: queda
      // prellenado en este navegador y se muestra una vez en la app tras el alta.
      if (res.tenant?.slug) {
        setPrefill(res.tenant.slug, payload.email.trim().toLowerCase());
        try { sessionStorage.setItem(SIGNUP_SLUG_KEY, res.tenant.slug); } catch { /* sin storage */ }
      }
      setToken(res.accessToken);
      setRefreshToken(res.refreshToken);
      touchActivity();
      sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
      window.location.href = '/';
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    // Day 6 Block C: revoca el refresh en backend (best-effort, no bloquea).
    const rt = getRefreshToken();
    if (rt) {
      fetch(
        (import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/$/, '') + '/auth/logout',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: rt }),
        },
      ).catch(() => { /* swallow */ });
    }
    stopSilentRefresh();
    setToken(null);
    setRefreshToken(null);
    clearActivity();
    window.location.href = '/';
  }, []);

  // Day 6 Block C: si el refresh falla en mitad de la sesión → logout suave.
  useEffect(() => {
    const off = onAuthLost(() => {
      setToken(null);
      setRefreshToken(null);
      clearActivity();
      window.location.href = '/';
    });
    return () => { off(); };
  }, []);

  // -------------------------------------------------------------------------
  // Carril K — Idle / session timeout.
  // Tras la VENTANA de inactividad (base 1h / "recordar" 12h; ver getIdleWindowMs)
  // → logout (revoca refresh + redirige a login). Persistimos la última actividad
  // en localStorage para que reabrir la app desde el historial tras el límite
  // TAMBIÉN exija login: la sesión no "revive" en silencio vía el refresh de 30 días.
  // -------------------------------------------------------------------------
  const expireByIdle = useCallback(() => {
    sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1');
    logout(); // revoca el refresh en backend + limpia tokens/actividad + redirige
  }, [logout]);

  useEffect(() => {
    if (!token) return;
    // Marca inicial: el user acaba de (re)abrir la app autenticado.
    touchActivity();
    // Carril K — arranca el refresh PROACTIVO silencioso: renueva el access antes
    // de que expire (15m) → el uso activo nunca choca contra ese borde.
    scheduleSilentRefresh();

    // Throttle: no escribimos en localStorage en cada mousemove, solo cada 15s.
    let lastTouch = Date.now();
    const onActivity = () => {
      const now = Date.now();
      if (now - lastTouch > 15_000) { lastTouch = now; touchActivity(); }
    };
    const events: Array<keyof WindowEventMap> = [
      'mousedown', 'keydown', 'scroll', 'touchstart', 'click',
    ];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    // Chequeo periódico: si superó el límite, cerrar sesión. El intervalo corre
    // aunque no haya requests en vuelo (a diferencia del refresh interceptor).
    const interval = window.setInterval(() => {
      if (isSessionExpiredByIdle()) expireByIdle();
    }, 20_000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      window.clearInterval(interval);
      stopSilentRefresh();
    };
  }, [token, expireByIdle]);

  const value = useMemo<AuthCtx>(
    () => ({ token, user, loading, login, signup, logout, refresh }),
    [token, user, loading, login, signup, logout, refresh],
  );
  return (
    <Ctx.Provider value={value}>
      {children}
      {/* §31.2 riesgo B — aviso de expiración 5 min antes (protege la pantalla de
          cocina pasiva y cualquier vista sin tocar). */}
      {token && <SessionExpiryWarning />}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);
